import { useState, useEffect } from 'react';
import { auditAPI } from '../../api';
import styles from './Audit.module.css';

const MODULES = ['assets','work_orders','maintenance','compliance','settings','auth','spares'];
const MODULE_COLORS = {
  assets:      {bg:'#eff4ff',color:'#1a56db'},
  work_orders: {bg:'#f0fdf4',color:'#16a34a'},
  maintenance: {bg:'#fef9c3',color:'#a16207'},
  compliance:  {bg:'#fdf4ff',color:'#9333ea'},
  settings:    {bg:'#f1f5f9',color:'#475569'},
  auth:        {bg:'#fff7ed',color:'#c2410c'},
  spares:      {bg:'#f0fdf4',color:'#0891b2'},
};
const ACTION_ICONS = { create:'＋', update:'✏', delete:'✕', login:'→', logout:'←', status_change:'↻', update_org:'⚙' };

function timeAgo(dt) {
  if (!dt) return '—';
  const sec = Math.floor((Date.now() - new Date(dt)) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec/60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec/3600)}h ago`;
  return `${Math.floor(sec/86400)}d ago`;
}

function DetailPanel({ log }) {
  if (!log) return (
    <div className={styles.detail_empty}>
      <div style={{fontSize:36}}>📋</div>
      <div>Select a log entry to view details</div>
    </div>
  );
  const mc = MODULE_COLORS[log.module] || {bg:'#f1f5f9',color:'#64748b'};
  const icon = ACTION_ICONS[log.action] || '•';
  return (
    <div className={styles.detail_panel}>
      <div className={styles.dp_header}>
        <span className={styles.badge} style={{background:mc.bg,color:mc.color}}>{log.module?.replace(/_/g,' ')}</span>
        <span style={{fontSize:12,color:'#94a3b8'}}>{timeAgo(log.created_at)}</span>
      </div>
      <div className={styles.dp_title}>{icon} {log.action?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</div>
      <div className={styles.dp_divider}/>
      <div className={styles.dp_section}>Log Details</div>
      {[
        ['User', log.user_name || 'System'],
        ['Module', log.module?.replace(/_/g,' ')],
        ['Action', log.action?.replace(/_/g,' ')],
        ['Reference', log.record_ref],
        ['Record ID', log.record_id ? `#${log.record_id}` : null],
        ['IP Address', log.ip_address],
        ['Timestamp', log.created_at?.slice(0,19)?.replace('T',' ')],
      ].filter(([,v])=>v).map(([l,v])=>(
        <div key={l} className={styles.dp_row}><span>{l}</span><strong>{v}</strong></div>
      ))}
    </div>
  );
}

export default function AuditPage() {
  const [logs,     setLogs]    = useState([]);
  const [loading,  setLoading] = useState(true);
  const [selected, setSelected]= useState(null);
  const [search,   setSearch]  = useState('');
  const [modFilter,setModFilter]= useState('');

  useEffect(()=>{
    setLoading(true);
    const p = Object.fromEntries(Object.entries({q:search,module:modFilter}).filter(([,v])=>v));
    auditAPI.list(p).then(r=>setLogs(r.data.data)).finally(()=>setLoading(false));
  },[search,modFilter]);

  const modCounts = logs.reduce((acc,l)=>({...acc,[l.module]:(acc[l.module]||0)+1}),{});

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.search_wrap}>
          <span className={styles.search_icon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className={styles.search_input} placeholder="Search by reference, user…" value={search} onChange={e=>setSearch(e.target.value)}/>
          {search && <button className={styles.search_clear} onClick={()=>setSearch('')}>✕</button>}
        </div>
        <select className={styles.toolbar_select} value={modFilter} onChange={e=>setModFilter(e.target.value)}>
          <option value="">All Modules</option>
          {MODULES.map(m=><option key={m} value={m}>{m.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
        </select>
        <div className={styles.toolbar_right}><span className={styles.total_badge}>{logs.length} entries</span></div>
      </div>

      <div className={styles.body}>
        <div className={styles.filter_col}>
          <div className={`${styles.filter_item} ${modFilter===''?styles.filter_active:''}`} onClick={()=>setModFilter('')}>
            <span className={styles.filter_dot} style={{background:'#1a56db'}}/>
            <span className={styles.filter_label}>All Activity</span>
            <span className={`${styles.filter_count} ${modFilter===''?styles.filter_count_active:''}`}>{logs.length}</span>
          </div>
          <div className={styles.filter_divider}/>
          <div className={styles.filter_section}>By Module</div>
          {MODULES.filter(m=>modCounts[m]).map(m=>{
            const mc = MODULE_COLORS[m]||{bg:'#f1f5f9',color:'#64748b'};
            return (
              <div key={m} className={`${styles.filter_item} ${modFilter===m?styles.filter_active:''}`} onClick={()=>setModFilter(modFilter===m?'':m)}>
                <span className={styles.filter_dot} style={{background:mc.color}}/>
                <span className={styles.filter_label}>{m.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</span>
                <span className={`${styles.filter_count} ${modFilter===m?styles.filter_count_active:''}`}>{modCounts[m]||0}</span>
              </div>
            );
          })}
          {(search||modFilter) && <button className={styles.reset_btn} onClick={()=>{setSearch('');setModFilter('');}}>✕ Reset Filters</button>}
        </div>

        <div className={styles.list_col}>
          {loading ? <div className={styles.loading}>Loading…</div>
          : logs.length === 0 ? (
            <div className={styles.empty}><div className={styles.empty_icon}>📋</div><div className={styles.empty_title}>No audit logs found</div></div>
          ) : logs.map((log,i) => {
            const mc = MODULE_COLORS[log.module] || {bg:'#f1f5f9',color:'#64748b'};
            const icon = ACTION_ICONS[log.action] || '•';
            const isSel = selected?.id === log.id;
            return (
              <div key={i} className={`${styles.card} ${isSel?styles.card_active:''}`} onClick={()=>setSelected(log)}>
                <div className={styles.card_stripe} style={{background:mc.color}}/>
                <div className={styles.card_body}>
                  <div className={styles.card_top}>
                    <span className={styles.badge} style={{background:mc.bg,color:mc.color}}>{log.module?.replace(/_/g,' ')}</span>
                    <span style={{fontSize:11,color:'#94a3b8'}}>{timeAgo(log.created_at)}</span>
                  </div>
                  <div className={styles.card_title}>{icon} {log.action?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</div>
                  <div className={styles.card_meta}>{log.user_name||'System'} {log.record_ref ? `· ${log.record_ref}` : ''}</div>
                  <div className={styles.card_footer}>
                    <span style={{fontSize:11,color:'#94a3b8',fontFamily:'monospace'}}>{log.created_at?.slice(0,19)?.replace('T',' ')}</span>
                    {log.ip_address && <span style={{fontSize:11,color:'#94a3b8'}}>{log.ip_address}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.detail_col}><DetailPanel log={selected}/></div>
      </div>
    </div>
  );
}
